import { ChevronRightIcon, LinkIcon } from 'lucide-react';
import { type FC, useRef } from 'react';
import { ErrorMessage, Form, Formik } from 'formik';
import { useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';

import { Button } from '@src/design-system/button';
import {
  DialogBody,
  DialogClose,
  DialogFooter,
  DialogHeader,
  DialogSub,
  DialogSubContent,
  DialogSubTrigger,
} from '@src/design-system/dialog';
import { inputVariants } from '@src/design-system/input';
import { cn } from '@src/design-system/helpers';
import { Label } from '@src/design-system/label';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@src/design-system/tabs';

import {
  APPOINTMENTS_PLATFORMS,
  APPOINTMENTS_TYPES,
  type TAppointment,
} from '@shared/types/appointment';

import { CustomErrorMessage } from '@src/design-system/error-message';

import { SharedAppointmentFields } from './shared-appointment-fields';

import { ChooseInterviewerSubModal } from '@features/participants/pages/detail/components/participant-tabs/appointments-tab/sub-modals/choose-interviewer-sub-modal';
import { CreateInterviewerSubModal } from '@features/participants/pages/detail/components/participant-tabs/appointments-tab/sub-modals/create-interviewer-sub-modal';
import { InterviewerInfo } from '@features/participants/pages/detail/components/participant-tabs/appointments-tab/interviewer-info';

import { UserAddIcon } from '@shared/assets/icons/shared/user-add-icon';
import { UserSearchIcon } from '@shared/assets/icons/shared/user-search-icon';
import {
  appointmentFormInitialValues,
  appointmentFormNames,
  appointmentFormSchema,
  createInitialValueFromExistingAppointment,
  type TAppointmentFormValues,
} from '@shared/forms/appointment';
import { FormikCountryField } from '@shared/fields/country-field/formik-country-field';
import { Textfield } from '@shared/fields/text-field';
import { InputWithSelectField } from '@shared/fields/input-with-select-field';
import { useHasPermission } from '@shared/hooks/use-has-permission';
import { entities } from '@shared/constants/entities';
import meetingApi from '@src/features/participants/services/meeting-api';
import { useDialogContext } from '@src/design-system/dialog/use-dialog-context';

const {
  appointmentType,
  host,
  meetingLink,
  meetingPlatform,
  country,
  location,
} = appointmentFormNames;

export const CreateAppointmentModal: FC<{
  editableMeetingObj?: TAppointment;
  onSuccessClose?: (id: number) => void;
  lead_id?: number;
}> = ({ editableMeetingObj, onSuccessClose, lead_id }) => {
  const { formikRef } = useDialogContext();

  const { leadId } = useParams() as { leadId: string };
  const { mutate: createAppointment, isPending } = meetingApi.useCreate();
  const { mutate: editAppointment, isPending: isEditPending } =
    meetingApi.useUpdate();

  const hasPermission = useHasPermission();

  const closeRef = useRef<HTMLButtonElement>(null);

  const subModals = [
    {
      icon: <UserSearchIcon />,
      label: 'Choose interviewer',
      subModalContent: <ChooseInterviewerSubModal />,
      isAllowed: hasPermission(entities.interviewer, 'view'),
      onClickWhenNotAllowed: () =>
        toast.error("you don't have permission view interviewers"),
    },
    {
      icon: <UserAddIcon />,
      label: 'Add interviewer',
      subModalContent: <CreateInterviewerSubModal />,
      isAllowed: hasPermission(entities.interviewer, 'add'),
      onClickWhenNotAllowed: () =>
        toast.error("you don't have permission add interviewer"),
    },
  ];

  const handleSubmit = ({
    country,
    city,
    host,
    location,
    meetingLink,
    meetingPlatform,
    date,
    time,
    appointmentType,
    interviewType,
    ...restValues
  }: TAppointmentFormValues) => {
    const isOnline = appointmentType === APPOINTMENTS_TYPES.online;

    const meetingData = {
      type: appointmentType,
      host: host.id,
      lead: leadId ? +leadId : lead_id,
      ...restValues,
      ...(isOnline
        ? { link: meetingLink, platform: meetingPlatform }
        : { country, city, location }),
      interview_type: interviewType,
      start_time: `${date}T${time}`,
    };

    if (editableMeetingObj) {
      editAppointment(
        {
          id: editableMeetingObj.id,
          ...meetingData,
        },
        {
          onSuccess: () => closeRef.current?.click(),
        }
      );
    } else {
      createAppointment(meetingData, {
        onSuccess: (response) => {
          onSuccessClose?.(response.id);
          closeRef.current?.click();
        },
      });
    }
  };

  return (
    <Formik
      innerRef={formikRef}
      validationSchema={appointmentFormSchema}
      initialValues={
        createInitialValueFromExistingAppointment(editableMeetingObj) || {
          ...appointmentFormInitialValues,
          interviewType: lead_id
            ? 'SCREENING_INTERVIEW'
            : appointmentFormInitialValues.interviewType,
        }
      }
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ values, setFieldValue, setTouched }) => {
        return (
          <Form className='flex flex-col h-full'>
            <DialogHeader>
              {editableMeetingObj ? 'Edit Appointment' : 'Schedule Appointment'}
            </DialogHeader>

            <DialogBody>
              <Tabs
                onValueChange={(newValue) => {
                  setFieldValue(appointmentType, newValue);
                  setTouched({});
                }}
                value={values.appointmentType}
              >
                <div className='mb-6'>
                  <Label>Appointment Type</Label>
                  <div className='flex gap-2'>
                    <TabsList className='border-0 pb-0 h-auto gap-2 '>
                      {Object.entries(APPOINTMENTS_TYPES).map(
                        ([label, value]) => (
                          <TabsTrigger
                            key={value}
                            value={value}
                            className={cn(
                              `px-4 py-2 border capitalize transition-none data-[state=active]:bg-primary-100 cursor-pointer data-[state=active]:border data-[state=active]:border-pale-border border-pale-border text-gray-600 rounded-full text-sm`
                            )}
                          >
                            {label}
                          </TabsTrigger>
                        )
                      )}
                    </TabsList>
                  </div>
                </div>

                <SharedAppointmentFields />

                <TabsContent value={APPOINTMENTS_TYPES.online}>
                  <div className='mb-6'>
                    <Label>Meeting Link</Label>
                    <InputWithSelectField
                      inputProps={{
                        type: 'url',
                        placeholder: 'ex: https://meet.google.com/fxf...',
                        prefixIcon: <LinkIcon size={16} />,
                      }}
                      inputName={meetingLink}
                      selectName={meetingPlatform}
                      options={Object.values(APPOINTMENTS_PLATFORMS)}
                      selectPlaceholder='Select platform'
                    />
                  </div>
                </TabsContent>

                <TabsContent value={APPOINTMENTS_TYPES.onsite}>
                  <div className='grid grid-cols-[2fr_1fr] gap-4 mb-6'>
                    <div className='flex-1 w-full'>
                      <Label>Country</Label>
                      <FormikCountryField
                        selectName={country}
                        isInModal
                      />
                    </div>

                    <Textfield
                      name='city'
                      label='City'
                      placeholder='e.g. Cairo'
                    />
                  </div>

                  <Textfield
                    name={location}
                    label='Location Link'
                    className='mb-6'
                    type='url'
                    placeholder='e.g. Google maps'
                    prefixIcon={<LinkIcon size={16} />}
                  />
                </TabsContent>
              </Tabs>

              <Label>Interviewer Details</Label>
              <DialogSub>
                {subModals.map(
                  ({
                    icon,
                    isAllowed,
                    label,
                    onClickWhenNotAllowed,
                    subModalContent,
                  }) => (
                    <div
                      key={label}
                      className={'first:mb-2'}
                    >
                      <DialogSubTrigger
                        value={label}
                        onClick={() => {
                          if (isAllowed) return;
                          onClickWhenNotAllowed();
                        }}
                        className={cn(
                          inputVariants({ variant: 'fill', size: 'lg' }),
                          'flex text-sm min-w-full justify-between items-center cursor-pointer'
                        )}
                      >
                        <span className='flex items-center gap-2'>
                          {icon}
                          {label}
                        </span>
                        <ChevronRightIcon size={16} />
                      </DialogSubTrigger>

                      {isAllowed && (
                        <DialogSubContent value={label}>
                          {subModalContent}
                        </DialogSubContent>
                      )}
                    </div>
                  )
                )}
              </DialogSub>

              {values.host && (
                <InterviewerInfo
                  companyName={values.host.company_name}
                  jobTitle={values.host.job_title}
                  name={values.host.name}
                  id={values.host.id}
                  email={values.host.email}
                  avatar={values.host.avatar}
                  className='border border-pale-border mt-4 cursor-auto'
                />
              )}
              <ErrorMessage
                name={host}
                component={CustomErrorMessage}
              />
            </DialogBody>

            <DialogFooter className='mt-auto'>
              <Button
                isLoading={isPending || isEditPending}
                className='flex-1'
                type='submit'
              >
                {editableMeetingObj ? 'Edit' : 'Save'}
              </Button>
              <DialogClose asChild>
                <Button
                  ref={closeRef}
                  className='flex-1'
                  variant='primaryOutline'
                >
                  Cancel
                </Button>
              </DialogClose>
            </DialogFooter>
          </Form>
        );
      }}
    </Formik>
  );
};
